<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import reportsAPI from 'dashboard/api/reports';
import { useMapGetter } from 'dashboard/composables/store';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Avatar from 'next/avatar/Avatar.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
  DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id,
];

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isTeamsEnabled = computed(() =>
  isFeatureEnabledOnAccount.value?.(
    accountId.value,
    FEATURE_FLAGS.TEAM_MANAGEMENT
  )
);

const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);
const isBreakdownOpen = ref(true);
const latestRefreshToken = ref(0);

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = refreshToken =>
  refreshToken === latestRefreshToken.value;

const filters = ref({
  from: 0,
  to: 0,
  userIds: [],
  labelId: null,
  inboxId: null,
  teamId: null,
  vipLevelId: null,
  issueId: null,
  priority: null,
});

const summary = ref({
  current: {},
  previous: {},
  current_period: {},
  previous_period: {},
});
const series = ref([]);
const topAgents = ref([]);
const emojiBreakdown = ref([]);

const EMOJI_VALUE_MAP = { 1: '😞', 2: '😑', 3: '😐', 4: '😀', 5: '😍' };
const EMOJI_LABEL_MAP = {
  1: 'Poor',
  2: 'Fair',
  3: 'Average',
  4: 'Good',
  5: 'Excellent',
};

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const formatInteger = value => Number(value || 0).toLocaleString();
const formatPercent = value => `${Number(value || 0).toFixed(2)}%`;
const formatRate = value => `${Math.round(Number(value || 0))}%`;

const currentSummary = computed(() => summary.value.current || {});
const previousSummary = computed(() => summary.value.previous || {});

const currentPeriodLabel = computed(() => {
  const period = summary.value.current_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const previousPeriodLabel = computed(() => {
  const period = summary.value.previous_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const chartRows = computed(() => series.value || []);
const hasChartData = computed(() => chartRows.value.length > 0);

const currentRate = computed(() =>
  Number(currentSummary.value.satisfaction_rate || 0)
);
const previousRate = computed(() =>
  Number(previousSummary.value.satisfaction_rate || 0)
);

const comparisonMetrics = computed(() => [
  {
    key: 'current-good',
    colorClass: 'is-current-good',
    label: t('CSAT_REPORTS.RATED_GOOD'),
    period: currentPeriodLabel.value,
    count: Number(currentSummary.value.rated_good_count || 0),
    percent: Number(currentSummary.value.rated_good_percentage || 0),
  },
  {
    key: 'previous-good',
    colorClass: 'is-previous-good',
    label: t('CSAT_REPORTS.RATED_GOOD'),
    period: previousPeriodLabel.value,
    count: Number(previousSummary.value.rated_good_count || 0),
    percent: Number(previousSummary.value.rated_good_percentage || 0),
  },
  {
    key: 'current-bad',
    colorClass: 'is-current-bad',
    label: t('CSAT_REPORTS.RATED_BAD'),
    period: currentPeriodLabel.value,
    count: Number(currentSummary.value.rated_bad_count || 0),
    percent: Number(currentSummary.value.rated_bad_percentage || 0),
  },
  {
    key: 'previous-bad',
    colorClass: 'is-previous-bad',
    label: t('CSAT_REPORTS.RATED_BAD'),
    period: previousPeriodLabel.value,
    count: Number(previousSummary.value.rated_bad_count || 0),
    percent: Number(previousSummary.value.rated_bad_percentage || 0),
  },
]);

const chartCollection = computed(() => ({
  labels: chartRows.value.map(row => [
    format(fromUnixTime(row.current_timestamp), 'dd MMM'),
    format(fromUnixTime(row.previous_timestamp), 'dd MMM'),
  ]),
  datasets: [
    {
      label: `${t('CSAT_REPORTS.RATED_GOOD')} (${currentPeriodLabel.value})`,
      data: chartRows.value.map(row => Number(row.current_good_count || 0)),
      backgroundColor: '#22c55e',
      borderRadius: 3,
      categoryPercentage: 0.72,
      barPercentage: 0.82,
    },
    {
      label: `${t('CSAT_REPORTS.RATED_GOOD')} (${previousPeriodLabel.value})`,
      data: chartRows.value.map(row => Number(row.previous_good_count || 0)),
      backgroundColor: '#9be3b2',
      borderRadius: 3,
      categoryPercentage: 0.72,
      barPercentage: 0.82,
    },
    {
      label: `${t('CSAT_REPORTS.RATED_BAD')} (${currentPeriodLabel.value})`,
      data: chartRows.value.map(row => Number(row.current_bad_count || 0)),
      backgroundColor: '#ef4444',
      borderRadius: 3,
      categoryPercentage: 0.72,
      barPercentage: 0.82,
    },
    {
      label: `${t('CSAT_REPORTS.RATED_BAD')} (${previousPeriodLabel.value})`,
      data: chartRows.value.map(row => Number(row.previous_bad_count || 0)),
      backgroundColor: '#f3a5a5',
      borderRadius: 3,
      categoryPercentage: 0.72,
      barPercentage: 0.82,
    },
  ],
}));

const tooltipId = 'chat-satisfaction-tooltip';

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index => {
          const row = chartRows.value[index];
          if (!row) return '';
          return `${format(fromUnixTime(row.current_timestamp), 'dd MMM yy')} vs ${format(
            fromUnixTime(row.previous_timestamp),
            'dd MMM yy'
          )}`;
        },
        seriesLabelForDataset: datasetIndex => {
          if (datasetIndex === 0) return t('CSAT_REPORTS.RATED_GOOD');
          if (datasetIndex === 1)
            return `${t('CSAT_REPORTS.RATED_GOOD')} (${previousPeriodLabel.value})`;
          if (datasetIndex === 2) return t('CSAT_REPORTS.RATED_BAD');
          if (datasetIndex === 3)
            return `${t('CSAT_REPORTS.RATED_BAD')} (${previousPeriodLabel.value})`;
          return '';
        },
        percentDigits: 2,
        showPercent: true,
      }),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: '#edf1f5',
      },
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: '#6b7280',
      },
    },
  },
}));

const breakdownDayColumns = computed(() =>
  chartRows.value.map(row => ({
    key: row.current_timestamp,
    label: format(fromUnixTime(row.current_timestamp), 'dd MMM yy'),
  }))
);

const breakdownSeriesRows = computed(() => {
  const goodValues = chartRows.value.map(row =>
    Number(row.current_good_count || 0)
  );
  const badValues = chartRows.value.map(row =>
    Number(row.current_bad_count || 0)
  );
  const goodTotal = goodValues.reduce((sum, v) => sum + Number(v || 0), 0);
  const badTotal = badValues.reduce((sum, v) => sum + Number(v || 0), 0);

  return [
    {
      key: 'rated-good',
      label: t('CSAT_REPORTS.RATED_GOOD'),
      values: goodValues,
      total: goodTotal,
    },
    {
      key: 'rated-bad',
      label: t('CSAT_REPORTS.RATED_BAD'),
      values: badValues,
      total: badTotal,
    },
  ];
});

const topAgentRows = computed(() =>
  (topAgents.value || []).map((agent, index) => ({
    rank: index + 1,
    ...agent,
  }))
);

const hasEmojiBreakdown = computed(() =>
  emojiBreakdown.value.some(item => item.count > 0)
);

const emojiBreakdownRows = computed(() =>
  emojiBreakdown.value.map(item => ({
    ...item,
    emoji: EMOJI_VALUE_MAP[item.value] || '',
    label: EMOJI_LABEL_MAP[item.value] || `Level ${item.value}`,
  }))
);

const fetchReport = async () => {
  if (!filters.value.from || !filters.value.to) return;

  const refreshToken = createRefreshToken();
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getChatSatisfaction({
      from: filters.value.from,
      to: filters.value.to,
      userIds: filters.value.userIds,
      labelId: filters.value.labelId,
      inboxId: filters.value.inboxId,
      teamId: filters.value.teamId,
      vipLevelId: filters.value.vipLevelId,
      issueId: filters.value.issueId,
      priority: filters.value.priority,
    });

    if (!isLatestRefresh(refreshToken)) return;

    summary.value = response.data?.summary || {
      current: {},
      previous: {},
      current_period: {},
      previous_period: {},
    };
    series.value = response.data?.series || [];
    topAgents.value = response.data?.top_agents || [];
    emojiBreakdown.value = response.data?.emoji_breakdown || [];
    generatedAt.value = new Date();
  } catch {
    if (!isLatestRefresh(refreshToken)) return;
    errorMessage.value = t('CSAT_REPORTS.FETCH_ERROR');
    summary.value = {
      current: {},
      previous: {},
      current_period: {},
      previous_period: {},
    };
    series.value = [];
    topAgents.value = [];
    emojiBreakdown.value = [];
  } finally {
    if (isLatestRefresh(refreshToken)) {
      isLoading.value = false;
    }
  }
};

const onFilterChange = ({
  from,
  to,
  selectedAgents = [],
  selectedLabel = null,
  selectedInbox = null,
  selectedTeam = null,
  selectedVip = null,
  selectedIssue = null,
  selectedPriority = null,
}) => {
  filters.value = {
    from,
    to,
    userIds: Array.isArray(selectedAgents)
      ? selectedAgents.map(agent => agent.id)
      : [],
    labelId: selectedLabel?.id || null,
    inboxId: selectedInbox?.id || null,
    teamId: selectedTeam?.id || null,
    vipLevelId: selectedVip?.id || null,
    issueId: selectedIssue?.id || null,
    priority: selectedPriority?.id || null,
  };
  fetchReport();
};

const downloadBreakdownCsv = () => {
  const headers = [
    t('CSAT_REPORTS.SERIES'),
    ...breakdownDayColumns.value.map(col => col.label),
    'Total',
  ];

  const rows = breakdownSeriesRows.value.map(row => [
    row.label,
    ...row.values.map(v => formatInteger(v)),
    formatInteger(row.total),
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'chat-satisfaction-breakdown',
    to: filters.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const generatedAtLabel = computed(() =>
  generatedAt.value
    ? generatedAt.value.toLocaleString()
    : t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET')
);

const profileHrefForEmail = email =>
  email ? `/team/agent/${encodeURIComponent(email)}` : '#';

const hasProfileLink = email => !!email;

onBeforeUnmount(() => {
  removeTooltipEl(tooltipId);
});
</script>

<template>
  <div class="cs-page">
    <div class="cs-shell">
      <div class="cs-header">
        <div class="cs-header-left">
          <h1 class="cs-title">
            {{ t('CSAT_REPORTS.HEADER') }}
          </h1>
        </div>
      </div>

      <div class="cs-toolbar">
        <div class="cs-toolbar-left">
          <CompactDateInboxFilters
            :allowed-date-range-ids="SUPPORTED_RANGE_IDS"
            show-agents
            show-labels
            :show-teams="isTeamsEnabled"
            show-vip
            show-issue
            show-priority
            livechat-only-inboxes
            hide-inactive-filters
            @filter-change="onFilterChange"
          />
        </div>
      </div>

      <div class="cs-content">
        <div v-if="errorMessage" class="cs-error">{{ errorMessage }}</div>

        <section class="cs-card">
          <div class="cs-card-header">
            <div class="cs-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="cs-card-title">{{ t('CSAT_REPORTS.HEADER') }}</h2>
            </div>
          </div>

          <div class="cs-section cs-section--no-bottom">
            <div class="cs-overview-grid">
              <div class="cs-rate-panel">
                <div class="cs-rate-card">
                  <div class="cs-rate-ring">
                    <svg viewBox="0 0 42 42" class="cs-rate-ring-svg">
                      <circle
                        class="cs-rate-ring-track"
                        cx="21"
                        cy="21"
                        r="15.9155"
                      />
                      <circle
                        class="cs-rate-ring-fill cs-rate-ring-fill--current"
                        cx="21"
                        cy="21"
                        r="15.9155"
                        :stroke-dasharray="`${currentRate}, 100`"
                      />
                    </svg>
                    <div class="cs-rate-ring-label">
                      {{ formatRate(currentRate) }}
                    </div>
                  </div>
                  <div class="cs-rate-copy">
                    <div class="cs-rate-copy-title">
                      {{ t('CSAT_REPORTS.AVERAGE_SATISFACTION_RATE') }}
                    </div>
                    <div class="cs-rate-copy-period">
                      {{ currentPeriodLabel }}
                    </div>
                  </div>
                </div>

                <div class="cs-rate-card">
                  <div class="cs-rate-ring">
                    <svg viewBox="0 0 42 42" class="cs-rate-ring-svg">
                      <circle
                        class="cs-rate-ring-track"
                        cx="21"
                        cy="21"
                        r="15.9155"
                      />
                      <circle
                        class="cs-rate-ring-fill cs-rate-ring-fill--previous"
                        cx="21"
                        cy="21"
                        r="15.9155"
                        :stroke-dasharray="`${previousRate}, 100`"
                      />
                    </svg>
                    <div class="cs-rate-ring-label">
                      {{ formatRate(previousRate) }}
                    </div>
                  </div>
                  <div class="cs-rate-copy">
                    <div class="cs-rate-copy-title">
                      {{ t('CSAT_REPORTS.AVERAGE_SATISFACTION_RATE') }}
                    </div>
                    <div class="cs-rate-copy-period">
                      {{ previousPeriodLabel }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="cs-comparison-grid">
                <div
                  v-for="metric in comparisonMetrics"
                  :key="metric.key"
                  class="cs-compare-card"
                >
                  <div class="cs-compare-header">
                    <div class="cs-compare-dot" :class="metric.colorClass" />
                    <div class="cs-compare-label">{{ metric.label }}</div>
                  </div>
                  <div class="cs-compare-period">{{ metric.period }}</div>
                  <div class="cs-compare-count-row">
                    <span class="cs-compare-count">{{
                      formatInteger(metric.count)
                    }}</span>
                    <span class="cs-compare-percent"
                      >({{ formatPercent(metric.percent) }})</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <div class="cs-chart">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />
              <div v-else-if="!hasChartData" class="cs-empty">
                {{ t('CSAT_REPORTS.NO_RECORDS') }}
              </div>
              <BarChart
                v-else
                :collection="chartCollection"
                :chart-options="chartOptions"
              />
            </div>
          </div>
        </section>

        <section class="cs-card">
          <div
            class="cs-breakdown-header"
            role="button"
            tabindex="0"
            @click="isBreakdownOpen = !isBreakdownOpen"
            @keydown.enter.prevent="isBreakdownOpen = !isBreakdownOpen"
            @keydown.space.prevent="isBreakdownOpen = !isBreakdownOpen"
          >
            <div class="cs-breakdown-title">
              <svg
                class="cs-breakdown-caret"
                :class="{ 'is-collapsed': !isBreakdownOpen }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m6 9 6 6 6-6"
                />
              </svg>
              <span>{{ t('CSAT_REPORTS.BREAKDOWN_TITLE') }}</span>
            </div>
            <button
              class="cs-export-btn"
              type="button"
              @click.stop="downloadBreakdownCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('CSAT_REPORTS.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div v-show="isBreakdownOpen" class="cs-table-wrap">
            <table class="cs-table">
              <thead>
                <tr>
                  <th class="cs-th-series">
                    {{ t('CSAT_REPORTS.SERIES') }}
                  </th>
                  <th
                    v-for="col in breakdownDayColumns"
                    :key="`breakdown-header-${col.key}`"
                    class="cs-th-data"
                  >
                    <div>{{ col.label }}</div>
                  </th>
                  <th class="cs-th-data"><div>Total</div></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in breakdownSeriesRows" :key="row.key">
                  <td class="cs-td-series">{{ row.label }}</td>
                  <td
                    v-for="(value, idx) in row.values"
                    :key="`${row.key}-${idx}`"
                    class="cs-td-data"
                  >
                    <div class="cs-cell-main">{{ formatInteger(value) }}</div>
                  </td>
                  <td class="cs-td-data">
                    <div class="cs-cell-main">
                      {{ formatInteger(row.total) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Emoji Breakdown Section (only shown when emoji-style rating data exists) -->
        <section v-if="hasEmojiBreakdown" class="cs-card">
          <div class="cs-card-header">
            <div class="cs-card-title-wrap">
              <span class="cs-emoji-breakdown-icon" aria-hidden="true">😊</span>
              <h2 class="cs-card-title">
                PostChatForm · Emoji rating
              </h2>
            </div>
          </div>
          <div class="cs-section">
            <table class="cs-top-agents-table cs-emoji-table">
              <thead>
                <tr>
                  <th class="cs-ta-th">Rating</th>
                  <th class="cs-ta-th cs-ta-th--num">Count</th>
                  <th class="cs-ta-th cs-ta-th--num">%</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in emojiBreakdownRows"
                  :key="`emoji-row-${row.value}`"
                >
                  <td class="cs-ta-td">
                    <span class="cs-emoji-cell">
                      <span class="cs-emoji-char">{{ row.emoji }}</span>
                      <span class="cs-emoji-label">{{ row.label }}</span>
                    </span>
                  </td>
                  <td class="cs-ta-td cs-ta-td--num">
                    {{ formatInteger(row.count) }}
                  </td>
                  <td class="cs-ta-td cs-ta-td--num">
                    {{ formatPercent(row.percentage) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="cs-card">
          <div class="cs-card-header">
            <div class="cs-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="cs-card-title">
                {{ t('CSAT_REPORTS.TOP_RATED_AGENTS_TITLE') }}
              </h2>
            </div>
          </div>

          <div class="cs-section">
            <div v-if="!topAgentRows.length" class="cs-empty cs-empty--short">
              {{ t('CSAT_REPORTS.NO_RECORDS') }}
            </div>

            <div v-else class="cs-top-agents-table-wrap">
              <table class="cs-top-agents-table">
                <thead>
                  <tr>
                    <th class="cs-ta-th cs-ta-th--rank">#</th>
                    <th class="cs-ta-th">
                      {{ t('CSAT_REPORTS.COLUMN_AGENTS') }}
                    </th>
                    <th class="cs-ta-th cs-ta-th--num">
                      {{ t('CSAT_REPORTS.COLUMN_RATED_CHATS') }}
                    </th>
                    <th class="cs-ta-th cs-ta-th--num">
                      {{ t('CSAT_REPORTS.COLUMN_RATED_GOOD') }}
                    </th>
                    <th class="cs-ta-th cs-ta-th--num">
                      {{ t('CSAT_REPORTS.COLUMN_RATED_BAD') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="agent in topAgentRows"
                    :key="`top-agent-${agent.agent_id}`"
                  >
                    <td class="cs-ta-td cs-ta-td--rank">{{ agent.rank }}</td>
                    <td class="cs-ta-td">
                      <div class="cs-agent">
                        <Avatar
                          :src="agent.agent_thumbnail"
                          :name="agent.agent_name"
                          :size="32"
                          rounded-full
                          hide-offline-status
                        />
                        <div class="cs-agent__info">
                          <a
                            v-if="hasProfileLink(agent.agent_email)"
                            :href="profileHrefForEmail(agent.agent_email)"
                            class="cs-agent__name"
                            :title="`View ${agent.agent_name} profile`"
                          >
                            {{ agent.agent_name }}
                          </a>
                          <span
                            v-else
                            class="cs-agent__name cs-agent__name--static"
                          >
                            {{ agent.agent_name }}
                          </span>
                          <span class="cs-agent__email">{{
                            agent.agent_email || '-'
                          }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="cs-ta-td cs-ta-td--num">
                      <span>
                        {{ formatInteger(agent.rated_chats_count) }}
                      </span>
                    </td>
                    <td class="cs-ta-td cs-ta-td--num">
                      <span class="cs-ta-inline">
                        <span
                          class="wlab-system-activity__icon wlab-system-activity__icon--good"
                          aria-hidden="true"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="m13.655 3.39-3.7 4.858c-.331.324-.519.772-.519 1.237v8.761c0 .965.805 1.754 1.788 1.754h5.006a1.78 1.78 0 0 0 1.635-1.061l2.914-6.674c.76-1.737-.536-3.666-2.458-3.666h-3.263l.85-4.017a1.3 1.3 0 0 0-.367-1.202 1.35 1.35 0 0 0-1.886.01M4.788 20c.983 0 1.788-.79 1.788-1.754V11.23c0-.965-.805-1.754-1.788-1.754-.984 0-1.788.789-1.788 1.754v7.016C3 19.211 3.804 20 4.788 20"
                            />
                          </svg>
                        </span>
                        <span class="cs-link--good">
                          {{ formatInteger(agent.rated_good_count) }}
                        </span>
                        <span class="cs-ta-sub"
                          >({{
                            formatPercent(agent.rated_good_percentage)
                          }})</span
                        >
                      </span>
                    </td>
                    <td class="cs-ta-td cs-ta-td--num">
                      <span class="cs-ta-inline">
                        <span
                          class="wlab-system-activity__icon wlab-system-activity__icon--bad"
                          aria-hidden="true"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="m13.655 20.61-3.7-4.858a1.74 1.74 0 0 1-.519-1.237V5.755c0-.965.805-1.754 1.788-1.754h5.006a1.78 1.78 0 0 1 1.635 1.061l2.914 6.674c.76 1.737-.536 3.666-2.458 3.666h-3.263l.85 4.017c.09.439-.045.886-.367 1.202a1.35 1.35 0 0 1-1.886-.01M4.788 4c.983 0 1.788.79 1.788 1.754v7.016c0 .965-.805 1.754-1.788 1.754-.984 0-1.788-.789-1.788-1.754V5.754C3 4.79 3.804 4 4.788 4"
                            />
                          </svg>
                        </span>
                        <span class="cs-link--bad">
                          {{ formatInteger(agent.rated_bad_count) }}
                        </span>
                        <span class="cs-ta-sub"
                          >({{
                            formatPercent(agent.rated_bad_percentage)
                          }})</span
                        >
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="cs-footer-note">
          {{ t('CSAT_REPORTS.GENERATED_AT', { time: generatedAtLabel }) }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cs-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}
.cs-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
}

.cs-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}

.cs-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.cs-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.cs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 3px 10px -6px;
  position: sticky;
  top: 58px;
  z-index: 49;
}

.cs-toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1 1 auto;
}
.cs-content {
  padding: 16px 32px;
}

.css-18rvhm8 {
  position: relative;
  border-radius: 4px;
  border: solid 1px #e5e5e6;
  background-color: #fff;
}

.css-p0dm1 {
  padding: 20px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #131317;
  display: flex;
  align-items: center;
  gap: 6px;
}

.css-itmny7 {
  padding: 0 5px;
  display: inline-block;
  vertical-align: middle;
  color: #8d8d95;
}

.css-cssveg {
  position: relative;
}

.css-1hrtwbc {
  position: sticky;
  z-index: 1;
  top: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  box-shadow:
    0 1px 4px 0 rgba(221, 226, 230, 0.6),
    inset 0 -1px 0 0 rgba(216, 222, 229, 0.85),
    inset 0 1px 0 0 rgba(216, 222, 229, 0.85);
  background-color: #fff;
  height: 35px;
}

.css-1esjc2m {
  flex: 0 0 54px;
  text-align: center;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: #8d8d95;
}

.css-1f4029p {
  flex: 1 0 0%;
  min-width: 0;
  padding-right: 7px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: #8d8d95;
}

.css-v5ev5j {
  flex: 0 0 20%;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: #8d8d95;
}

.css-1mystok {
  display: flex;
  flex-direction: row;
  align-items: center;
  border-bottom: solid 1px rgba(216, 222, 229, 0.85);
  min-height: 50px;
}

.css-1edx6oh {
  flex: 0 0 54px;
  text-align: center;
  color: #131317;
  font-weight: 600;
}

.css-1dyjd8k {
  flex: 1 0 0%;
  min-width: 0;
  padding-right: 7px;
}

.css-o2j9ze {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.css-wx5lzu {
  flex-shrink: 0;
  position: relative;
}

.css-19j0hq4 {
  display: flex;
  flex-direction: column;
  margin-left: 6px;
  overflow: hidden;
  justify-content: center;
}

.css-uojqc5 {
  text-decoration: none;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 3px;
  color: #131317;
}

.css-pehblk {
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #62626d;
}

.css-rldmy3 {
  flex: 0 0 20%;
  word-break: break-word;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #131317;
  font-size: 13px;
}

.css-dtm71d {
  text-decoration: none;
  color: #131317;
  font-weight: 500;
}

.css-osui6b {
  text-decoration: none;
  color: #131317;
  margin: 0 2px;
}

.css-cfgmfn {
  font-weight: 400;
  color: #8d8d95;
  font-size: 12px;
}

.css-ak9o0l {
  font-size: 13px;
  line-height: 22px;
  margin: 8px;
  text-align: center;
  color: #ababb1;
}

/* Icon colors from snippet */
:deep(.lc-Icon-module__icon--success___Jbdc3) {
  color: #00893c;
}
:deep(.lc-Icon-module__icon--error___95ah2) {
  color: #ca0005;
}
/* ──────────────────────────────────────────────────────────────
   White report unified styles (match total_chats/chat_engagement)
   ────────────────────────────────────────────────────────────── */
.cs-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}
.cs-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.cs-export-btn:hover {
  background: #f9fafb;
}

.cs-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.cs-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.cs-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.cs-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.cs-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}

.cs-section {
  padding: 16px 20px 20px;
}
.cs-section--no-bottom {
  padding-bottom: 0;
}

/* ── overview: 两环在上、四指标在下（竖向布局，与 livechat.com 图 2 一致） ── */
.cs-overview-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 两个环：横向并排 */
.cs-rate-panel {
  display: flex;
  gap: 48px;
  align-items: center;
  flex-wrap: wrap;
}
.cs-rate-card {
  display: flex;
  align-items: center;
  gap: 16px;
}
.cs-rate-ring {
  position: relative;
  width: 90px;
  height: 90px;
  flex: 0 0 auto;
}
.cs-rate-ring-svg {
  width: 90px;
  height: 90px;
  transform: rotate(-90deg);
}
.cs-rate-ring-track {
  fill: none;
  stroke: #dde8df;
  stroke-width: 4;
}
.cs-rate-ring-fill {
  fill: none;
  stroke-width: 4;
  stroke-linecap: round;
  transition: stroke-dasharray 0.4s ease;
}
.cs-rate-ring-fill--current {
  stroke: #38c172;
}
.cs-rate-ring-fill--previous {
  stroke: #9acacd;
}
.cs-rate-ring-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  color: #111827;
}
.cs-rate-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.cs-rate-copy-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
  max-width: 130px;
}
.cs-rate-copy-period {
  font-size: 11px;
  color: #6b7280;
}

/* 四个指标块：单行4列，与圆环区用横线隔开 */
.cs-comparison-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
}
@media (max-width: 900px) {
  .cs-comparison-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 0;
  }
}
@media (max-width: 540px) {
  .cs-comparison-grid {
    grid-template-columns: 1fr;
  }
}

.cs-compare-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-right: 16px;
}
.cs-compare-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cs-compare-dot {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex: 0 0 auto;
}
.cs-compare-dot.is-current-good {
  background: #3dcb69;
}
.cs-compare-dot.is-previous-good {
  background: #a9e7ba;
}
.cs-compare-dot.is-current-bad {
  background: #ef4444;
}
.cs-compare-dot.is-previous-bad {
  background: #f3a5a5;
}
.cs-compare-label {
  font-size: 13px;
  line-height: 18px;
  color: #111827;
  font-weight: 600;
}
.cs-compare-period {
  font-size: 11px;
  line-height: 16px;
  color: #6b7280;
  padding-left: 20px;
}
.cs-compare-count-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding-left: 20px;
  margin-top: 4px;
}
.cs-compare-count {
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #111827;
}
.cs-compare-percent {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.cs-metric-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) {
  .cs-metric-row {
    grid-template-columns: 1fr;
  }
}
.cs-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.cs-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.cs-metric-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
}
.cs-metric-value-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.cs-metric-value {
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #111827;
}
.cs-metric-value--good {
  color: #16a34a;
}
.cs-metric-value--bad {
  color: #dc2626;
}

.cs-chart {
  height: 384px;
  margin-top: 24px;
}
.cs-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}
.cs-empty--short {
  height: 160px;
}

.cs-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}
.cs-breakdown-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.cs-breakdown-caret {
  transition: transform 0.18s ease;
}
.cs-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.cs-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}
.cs-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  color: #424d57;
}
.cs-th-series {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #111827;
}
.cs-th-data {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  background: #f8fafc;
  white-space: nowrap;
}
.cs-td-series {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 12px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
}
.cs-td-data {
  padding: 10px 12px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: top;
  white-space: nowrap;
}
.cs-cell-main {
  font-weight: 700;
  color: #111827;
}

.cs-top-agents-table-wrap {
  overflow-x: auto;
}
.cs-top-agents-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.cs-ta-th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  white-space: nowrap;
}
.cs-ta-th--rank {
  width: 54px;
  text-align: center;
}
.cs-ta-th--num {
  text-align: right;
}
.cs-ta-td {
  padding: 12px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: middle;
  background: #ffffff;
}
.cs-ta-td--rank {
  text-align: center;
  font-weight: 700;
  color: #111827;
}
.cs-ta-td--num {
  text-align: right;
  white-space: nowrap;
}
.cs-ta-sub {
  margin-left: 6px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}
.cs-ta-inline {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
}

.cs-agent {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.cs-agent__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.cs-agent__name {
  color: #111827;
  font-weight: 700;
  text-decoration: none;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cs-agent__name:hover {
  text-decoration: underline;
}
.cs-agent__name--static:hover {
  text-decoration: none;
}
.cs-agent__email {
  font-size: 12px;
  color: #6b7280;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cs-link {
  color: #111827;
  font-weight: 700;
  text-decoration: underline;
  text-underline-position: from-font;
}
.cs-link--good {
  color: #16a34a;
}
.cs-link--bad {
  color: #dc2626;
}

.wlab-system-activity__icon {
  margin-right: 7px;
  display: inline-flex;
  align-items: center;
}
.wlab-system-activity__icon svg {
  pointer-events: none;
}
.wlab-system-activity__icon--good {
  color: #00893c;
}
.wlab-system-activity__icon--bad {
  color: #ca0005;
}

.cs-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.cs-emoji-breakdown-icon {
  font-size: 18px;
  margin-right: 2px;
  line-height: 1;
}

.cs-emoji-table {
  max-width: 360px;
}

.cs-emoji-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.cs-emoji-char {
  font-size: 20px;
  line-height: 1;
}

.cs-emoji-label {
  font-size: 13px;
  color: #374151;
}

::global(body.dark) .cs-page,
::global(body.dark) .cs-shell,
::global(body.dark) .cs-card,
::global(body.dark) .cs-header,
::global(body.dark) .cs-toolbar,
::global(body.dark) .cs-th-series,
::global(body.dark) .cs-th-data,
::global(body.dark) .cs-td-series,
::global(body.dark) .cs-td-data,
::global(body.dark) .cs-ta-th,
::global(body.dark) .cs-ta-td,
::global(body.theme-mix) .cs-page,
::global(body.theme-mix) .cs-shell,
::global(body.theme-mix) .cs-card,
::global(body.theme-mix) .cs-header,
::global(body.theme-mix) .cs-toolbar,
::global(body.theme-mix) .cs-th-series,
::global(body.theme-mix) .cs-th-data,
::global(body.theme-mix) .cs-td-series,
::global(body.theme-mix) .cs-td-data,
::global(body.theme-mix) .cs-ta-th,
::global(body.theme-mix) .cs-ta-td {
  background: #ffffff !important;
  color: #111827 !important;
}

::global(body.dark) .cs-th-series *,
::global(body.dark) .cs-th-data *,
::global(body.dark) .cs-ta-th *,
::global(body.theme-mix) .cs-th-series *,
::global(body.theme-mix) .cs-th-data *,
::global(body.theme-mix) .cs-ta-th * {
  color: #111827 !important;
}
</style>
